import { useCallback } from 'react';
import {
  SearchSelectField,
  type SearchSelectMode,
  type SearchSelectOption,
  type SearchSelectPage,
} from '@/components/ui';
import { adminFetch } from '@/lib/apiClient';
import type { AdminMemberDetail, AdminMemberSummary } from '../hooks/useAdminUsersQuery';
import { mobileDisplay } from '../lib/mobileDisplay';

const PAGE_SIZE = 20;

interface ListEnvelope {
  success: boolean;
  data?: {
    items?: AdminMemberSummary[];
    content?: AdminMemberSummary[];
    totalElements?: number;
    number?: number;
    size?: number;
    meta?: { total: number; page: number; size: number };
  } | null;
}

interface DetailEnvelope {
  success: boolean;
  data?: AdminMemberDetail | null;
}

// 라벨 정책: 절대 사용자에 raw UUID 노출 금지. name 우선, 없으면 회원 ID → email.
// 셋 다 비어 있으면 '미상' placeholder + hint 로 mobileNumber 노출.
// QA-420 — 같은 이름의 회원을 가를 수 있게 회원 ID(loginId)를 이름 뒤에 붙인다 — "홍길동 (hong123)".
export const toOption = (m: AdminMemberSummary | AdminMemberDetail): SearchSelectOption => {
  const loginId = m.loginId?.trim();
  const name = m.name?.trim();
  const label = name ? (loginId ? `${name} (${loginId})` : name) : loginId || m.email?.trim() || '미상';
  const hint = [m.email, mobileDisplay(m.mobileNumber)].filter(Boolean).join(' · ') || undefined;
  return { value: m.userId, label, hint };
};

const fetchUsers = async (keyword: string, page: number): Promise<SearchSelectPage> => {
  const qs = new URLSearchParams({
    size: String(PAGE_SIZE),
    page: String(page),
    unmask: 'true',
  });
  if (keyword.trim()) qs.set('keyword', keyword.trim());
  const res = await adminFetch(`/api/admin/v1/users?${qs.toString()}`);
  if (!res.ok) throw new Error(`회원 검색 실패 (${res.status})`);
  const env = (await res.json()) as ListEnvelope;
  const d = env.data;
  const items = (d?.items ?? d?.content ?? []).map(toOption);
  const total = d?.totalElements ?? d?.meta?.total;
  const hasMore =
    total != null
      ? (page + 1) * PAGE_SIZE < total
      : items.length === PAGE_SIZE;
  return total != null ? { items, hasMore, total } : { items, hasMore };
};

const fetchUser = async (userId: string): Promise<SearchSelectOption | null> => {
  const res = await adminFetch(`/api/admin/v1/users/${userId}?unmask=true`);
  if (!res.ok) return null;
  const env = (await res.json()) as DetailEnvelope;
  if (!env.data) return null;
  return toOption(env.data);
};

interface SingleProps {
  multi?: false;
  value: string | null;
  onChange: (userId: string | null) => void;
}

interface MultiProps {
  multi: true;
  value: ReadonlyArray<string>;
  onChange: (userIds: ReadonlyArray<string>) => void;
}

type UserSearchSelectProps = (SingleProps | MultiProps) & {
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string | null;
  testId?: string;
  ariaLabel?: string;
  emptyHint?: string;
  /**
   * Multi 모드 전용. 'modal' 권장 — 30+ 회원 누적 선택 시 chip 행 레이아웃이
   * 깨지지 않도록 별도 다이얼로그에서 선택을 수행한다.
   */
  mode?: SearchSelectMode;
  modalTitle?: string;
};

export const UserSearchSelect = (props: UserSearchSelectProps) => {
  const loadOptions = useCallback(
    (q: string, page: number) => fetchUsers(q, page),
    [],
  );
  const resolveOption = useCallback(
    (v: string) => fetchUser(v),
    [],
  );

  const {
    placeholder = '이름·회원 ID·이메일·전화번호로 회원 검색',
    disabled,
    errorMessage,
    testId,
    ariaLabel,
    emptyHint,
    mode,
    modalTitle,
  } = props;

  if (props.multi) {
    // 다중 선택은 모달이 기본 (30+ 누적 시 chip layout 보호). 명시적으로 'inline' 호출 시 호환.
    const resolvedMode: SearchSelectMode = mode ?? 'modal';
    return (
      <SearchSelectField
        multi
        value={props.value}
        onChange={props.onChange}
        loadOptions={loadOptions}
        resolveOption={resolveOption}
        placeholder={placeholder}
        emptyMessage="검색 결과가 없습니다"
        loadingMessage="검색 중..."
        mode={resolvedMode}
        modalTitle={modalTitle ?? '회원 선택'}
        modalDescription="이름·회원 ID·이메일·전화번호로 검색해 수신할 회원을 추가합니다."
        {...(disabled !== undefined ? { disabled } : {})}
        {...(errorMessage !== undefined && errorMessage !== null ? { errorMessage } : {})}
        {...(testId !== undefined ? { testId } : {})}
        {...(ariaLabel !== undefined ? { ariaLabel } : {})}
        {...(emptyHint !== undefined ? { emptyHint } : {})}
      />
    );
  }

  return (
    <SearchSelectField
      value={props.value}
      onChange={props.onChange}
      loadOptions={loadOptions}
      resolveOption={resolveOption}
      placeholder={placeholder}
      emptyMessage="검색 결과가 없습니다"
      loadingMessage="검색 중..."
      {...(disabled !== undefined ? { disabled } : {})}
      {...(errorMessage !== undefined && errorMessage !== null ? { errorMessage } : {})}
      {...(testId !== undefined ? { testId } : {})}
      {...(ariaLabel !== undefined ? { ariaLabel } : {})}
    />
  );
};
